/**
 * OAuth vs API Key Mapping for All 200+ Integrations
 * 
 * Categorizes all integrations by authentication method:
 * - oauth: Supports OAuth (Sign in with X)
 * - api-key: API key only
 * - both: Supports both OAuth and API key
 * - credentials: Username/password or other credentials
 * - webhook: Webhook-based
 * - none: No authentication required
 */

export type AuthMethod = "oauth" | "api-key" | "both" | "credentials" | "webhook" | "none";

export interface IntegrationAuthConfig {
  method: AuthMethod;
  oauthProvider?: string; // Maps to OAuth provider ID if OAuth supported
  supportsOAuth: boolean;
  supportsApiKey: boolean;
  defaultMethod: "oauth" | "api-key"; // Preferred method
  scopes?: string[]; // OAuth scopes if applicable
}

/**
 * Integration Authentication Mapping
 * Batch 1: Identity, Auth, Security (20 integrations)
 */
export const INTEGRATION_AUTH_MAP: Partial<Record<string, IntegrationAuthConfig>> = {
  // Identity, Auth, Security
  oauth_google: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  oauth_apple: {
    method: "oauth",
    oauthProvider: "apple",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["name", "email"],
  },
  oauth_microsoft: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["User.Read", "offline_access"],
  },
  oauth_meta: {
    method: "oauth",
    oauthProvider: "meta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["email", "public_profile"],
  },
  sso_okta: {
    method: "oauth",
    oauthProvider: "okta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  sso_azure_ad: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["User.Read", "offline_access"],
  },
  sso_ping: {
    method: "oauth",
    oauthProvider: "ping",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  passkeys: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  webauthn: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  mfa_twilio: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  mfa_authy: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  mfa_totp: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  iam_auth0: {
    method: "both",
    oauthProvider: "auth0",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  iam_okta: {
    method: "oauth",
    oauthProvider: "okta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  iam_aws: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  secrets_aws_kms: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  secrets_gcp_kms: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  secrets_azure_vault: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  secrets_hashicorp_vault: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Payments, Banking, Finance - Batch 2
  stripe: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  stripe_billing: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  adyen: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  braintree: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  paypal: {
    method: "both",
    oauthProvider: "paypal",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["openid", "profile", "email"],
  },
  plaid: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  truelayer: {
    method: "oauth",
    oauthProvider: "truelayer",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["accounts", "transactions"],
  },
  tink: {
    method: "oauth",
    oauthProvider: "tink",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["accounts:read", "transactions:read"],
  },
  mx: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  chargebee: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  recurly: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  xero: {
    method: "oauth",
    oauthProvider: "xero",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["accounting.transactions", "accounting.contacts"],
  },
  quickbooks: {
    method: "oauth",
    oauthProvider: "quickbooks",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["com.intuit.quickbooks.accounting"],
  },
  myob: {
    method: "oauth",
    oauthProvider: "myob",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["CompanyFile"],
  },
  ramp: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  brex: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  airwallex: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  wise: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  avalara: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  taxjar: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Crypto & Trading - Batch 3
  polygon: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  iex: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  alpha_vantage: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  interactive_brokers: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  alpaca: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  tradier: {
    method: "oauth",
    oauthProvider: "tradier",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "trade"],
  },
  binance: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  coinbase: {
    method: "oauth",
    oauthProvider: "coinbase",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["wallet:accounts:read", "wallet:transactions:read"],
  },
  kraken: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  okx: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  bybit: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  fireblocks: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  bitgo: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  stripe_radar: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  sift: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  riskified: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // E-Commerce Platforms - Batch 4
  shopify: {
    method: "oauth",
    oauthProvider: "shopify",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read_products", "write_products", "read_orders"],
  },
  woocommerce: {
    method: "credentials",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  bigcommerce: {
    method: "oauth",
    oauthProvider: "bigcommerce",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["store_v2_products", "store_v2_orders"],
  },
  square: {
    method: "oauth",
    oauthProvider: "square",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["MERCHANT_PROFILE_READ", "PAYMENTS_READ"],
  },
  etsy: {
    method: "oauth",
    oauthProvider: "etsy",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["listings_r", "listings_w"],
  },
  amazon: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  ebay: {
    method: "oauth",
    oauthProvider: "ebay",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://api.ebay.com/oauth/api_scope"],
  },
  magento: {
    method: "oauth",
    oauthProvider: "magento",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["all"],
  },
  prestashop: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  opencart: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  squarespace: {
    method: "oauth",
    oauthProvider: "squarespace",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  wix: {
    method: "oauth",
    oauthProvider: "wix",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["STORES", "ORDERS"],
  },
  shippo: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  shipstation: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  easypost: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aftership: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  netsuite: {
    method: "oauth",
    oauthProvider: "netsuite",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["restlets", "rest_webservices"],
  },
  
  // Messaging, Voice, Comms - Batch 6
  twilio_sms: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  twilio_mms: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  vonage_sms: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  messagebird: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  twilio_voice: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  vonage_voice: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  telnyx: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  sendgrid: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  mailgun: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  postmark: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  ses: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  fcm: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  apns: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  onesignal: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  whatsapp: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  telegram: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  discord: {
    method: "oauth",
    oauthProvider: "discord",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["bot", "guilds", "messages.read"],
  },
  slack: {
    method: "oauth",
    oauthProvider: "slack",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["channels:read", "chat:write", "files:read"],
  },
  microsoft_teams: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/User.Read", "https://graph.microsoft.com/Chat.Read"],
  },
  zoom: {
    method: "oauth",
    oauthProvider: "zoom",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["meeting:write", "user:read"],
  },
  agora: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  daily: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  twilio_live: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  twilio_lookup: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  numverify: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Social, Ads, Growth - Batch 8
  facebook: {
    method: "oauth",
    oauthProvider: "meta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["pages_read_engagement", "pages_manage_posts"],
  },
  instagram: {
    method: "oauth",
    oauthProvider: "meta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["instagram_basic", "instagram_content_publish"],
  },
  meta_graph: {
    method: "oauth",
    oauthProvider: "meta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["pages_read_engagement", "pages_manage_posts"],
  },
  tiktok: {
    method: "oauth",
    oauthProvider: "tiktok",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["user.info.basic", "video.upload"],
  },
  tiktok_ads: {
    method: "oauth",
    oauthProvider: "tiktok",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["ad.promote"],
  },
  youtube: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/youtube.readonly", "https://www.googleapis.com/auth/youtube.upload"],
  },
  twitter: {
    method: "oauth",
    oauthProvider: "twitter",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["tweet.read", "tweet.write", "users.read"],
  },
  linkedin: {
    method: "oauth",
    oauthProvider: "linkedin",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["r_liteprofile", "r_emailaddress", "w_member_social"],
  },
  reddit: {
    method: "oauth",
    oauthProvider: "reddit",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "submit"],
  },
  pinterest: {
    method: "oauth",
    oauthProvider: "pinterest",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["boards:read", "pins:read", "pins:write"],
  },
  snapchat_ads: {
    method: "oauth",
    oauthProvider: "snapchat",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["snapchat-marketing-api"],
  },
  google_ads: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/adwords"],
  },
  meta_ads: {
    method: "oauth",
    oauthProvider: "meta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["ads_management", "ads_read"],
  },
  ga4: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/analytics.readonly"],
  },
  mixpanel: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  amplitude: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  segment: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  rudderstack: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  klaviyo: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  mailchimp: {
    method: "oauth",
    oauthProvider: "mailchimp",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["audience_read", "audience_write"],
  },
  braze: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Media, Entertainment, Content - Batch 9
  spotify: {
    method: "oauth",
    oauthProvider: "spotify",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["user-read-playback-state", "user-modify-playback-state", "playlist-read-private"],
  },
  apple_music: {
    method: "oauth",
    oauthProvider: "apple",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["music-user-library.read"],
  },
  soundcloud: {
    method: "oauth",
    oauthProvider: "soundcloud",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["non-expiring"],
  },
  twitch: {
    method: "oauth",
    oauthProvider: "twitch",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["user:read:email", "channel:read:stream_key"],
  },
  vimeo: {
    method: "oauth",
    oauthProvider: "vimeo",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["public", "private", "upload"],
  },
  youtube_upload: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/youtube.upload"],
  },
  podcast_apple: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  podcast_spotify: {
    method: "oauth",
    oauthProvider: "spotify",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["user-read-email", "user-read-private"],
  },
  
  // Maps, Geo, Travel, Local - Batch 10
  google_maps: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  mapbox: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  here_maps: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  openstreetmap: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  nominatim: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  openweather: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  meteostat: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  weatherkit: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  loqate: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  smarty: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  amadeus: {
    method: "oauth",
    oauthProvider: "amadeus",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  sabre: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  uber: {
    method: "oauth",
    oauthProvider: "uber",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["profile", "request"],
  },
  google_business_profile: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/business.manage"],
  },
  yelp: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // AI, ML, Search, Knowledge - Batch 11
  openai: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  anthropic: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  google_gemini: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_openai: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  whisper: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_speech: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  google_speech: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  google_vision: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_vision: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aws_rekognition: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  google_translate: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  deepl: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  microsoft_translate: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  pinecone: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  weaviate: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  milvus: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  qdrant: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  serpapi: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  bing_search: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  brave_search: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  crossref: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  semantic_scholar: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  gdelt: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  newsapi: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Cloud, Infrastructure, DevOps - Batch 13
  aws_ec2: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aws_s3: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aws_lambda: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aws_sqs: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  aws_sns: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  gcp_compute: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  gcp_storage: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  gcp_bigquery: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_arm: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_blob: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  azure_functions: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  vercel: {
    method: "oauth",
    oauthProvider: "vercel",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  netlify: {
    method: "oauth",
    oauthProvider: "netlify",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  cloudflare_dns: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  cloudflare_workers: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  cloudflare_waf: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  route53: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  docker_hub: {
    method: "credentials",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  kubernetes: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  github_actions: {
    method: "oauth",
    oauthProvider: "github",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["repo", "workflow"],
  },
  gitlab_ci: {
    method: "oauth",
    oauthProvider: "gitlab",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["api", "read_api"],
  },
  circleci: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  datadog: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  new_relic: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  sentry: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  launchdarkly: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Developer Platforms & Code - Batch 14
  github: {
    method: "oauth",
    oauthProvider: "github",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["repo", "read:user", "read:org"],
  },
  gitlab: {
    method: "oauth",
    oauthProvider: "gitlab",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["api", "read_api", "write_api"],
  },
  bitbucket: {
    method: "oauth",
    oauthProvider: "bitbucket",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["repository:read", "repository:write"],
  },
  npm: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  pypi: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  maven: {
    method: "credentials",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  github_packages: {
    method: "oauth",
    oauthProvider: "github",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read:packages", "write:packages"],
  },
  logtail: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  google_play: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/androidpublisher"],
  },
  app_store_connect: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Productivity, Documents, Work Management - Batch 15
  gmail: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/gmail.readonly", "https://www.googleapis.com/auth/gmail.send"],
  },
  google_calendar: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/calendar.readonly", "https://www.googleapis.com/auth/calendar.events"],
  },
  google_drive: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/drive.readonly", "https://www.googleapis.com/auth/drive.file"],
  },
  google_docs: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/documents.readonly", "https://www.googleapis.com/auth/documents"],
  },
  google_sheets: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/spreadsheets.readonly", "https://www.googleapis.com/auth/spreadsheets"],
  },
  google_workspace: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/admin.directory.user.readonly"],
  },
  microsoft_365: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/User.Read", "https://graph.microsoft.com/Mail.Read"],
  },
  outlook: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/Mail.Read", "https://graph.microsoft.com/Mail.Send"],
  },
  onedrive: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/Files.ReadWrite"],
  },
  sharepoint: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/Sites.ReadWrite.All"],
  },
  notion: {
    method: "oauth",
    oauthProvider: "notion",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "update"],
  },
  airtable: {
    method: "oauth",
    oauthProvider: "airtable",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["data.records:read", "data.records:write"],
  },
  trello: {
    method: "oauth",
    oauthProvider: "trello",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  asana: {
    method: "oauth",
    oauthProvider: "asana",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["default"],
  },
  jira: {
    method: "oauth",
    oauthProvider: "atlassian",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read:jira-work", "write:jira-work"],
  },
  confluence: {
    method: "oauth",
    oauthProvider: "atlassian",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read:confluence-content.all", "write:confluence-content.all"],
  },
  monday: {
    method: "oauth",
    oauthProvider: "monday",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["boards:read", "boards:write"],
  },
  clickup: {
    method: "oauth",
    oauthProvider: "clickup",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  
  // CRM, Sales, Support - Batch 17
  salesforce: {
    method: "oauth",
    oauthProvider: "salesforce",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["api", "refresh_token", "offline_access"],
  },
  hubspot: {
    method: "oauth",
    oauthProvider: "hubspot",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["contacts", "content", "files"],
  },
  zoho: {
    method: "oauth",
    oauthProvider: "zoho",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["ZohoCRM.modules.ALL"],
  },
  pipedrive: {
    method: "oauth",
    oauthProvider: "pipedrive",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  zendesk: {
    method: "oauth",
    oauthProvider: "zendesk",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  intercom: {
    method: "oauth",
    oauthProvider: "intercom",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  freshdesk: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  servicenow: {
    method: "oauth",
    oauthProvider: "servicenow",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["useraccount", "table"],
  },
  
  // Reviews, Reputation, Local Presence - Batch 18
  trustpilot: {
    method: "oauth",
    oauthProvider: "trustpilot",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  podium: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  birdeye: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  yext: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // HR, Payroll, Identity at Work - Batch 18
  workday: {
    method: "oauth",
    oauthProvider: "workday",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["wd.audit", "wd.worker"],
  },
  bamboohr: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  gusto: {
    method: "oauth",
    oauthProvider: "gusto",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  deputy: {
    method: "oauth",
    oauthProvider: "deputy",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["longlife"],
  },
  adp: {
    method: "oauth",
    oauthProvider: "adp",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["api"],
  },
  okta_scim: {
    method: "oauth",
    oauthProvider: "okta",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["scim"],
  },
  azure_ad_scim: {
    method: "oauth",
    oauthProvider: "microsoft",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://graph.microsoft.com/.default"],
  },
  
  // Education, Learning, Community - Batch 19
  coursera: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  udemy: {
    method: "oauth",
    oauthProvider: "udemy",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  discord_community: {
    method: "oauth",
    oauthProvider: "discord",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["bot", "guilds"],
  },
  patreon: {
    method: "oauth",
    oauthProvider: "patreon",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["identity", "campaigns"],
  },
  memberstack: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // IoT, Smart Home, Hardware - Batch 20
  home_assistant: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  philips_hue: {
    method: "oauth",
    oauthProvider: "hue",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  google_home: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/homegraph"],
  },
  alexa_skills: {
    method: "oauth",
    oauthProvider: "amazon",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["alexa::ask:skills:readwrite"],
  },
  smartthings: {
    method: "oauth",
    oauthProvider: "smartthings",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["r:devices:*", "x:devices:*"],
  },
  mqtt: {
    method: "credentials",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  
  // Vehicles, Telematics, Mobility - Batch 20
  geotab: {
    method: "oauth",
    oauthProvider: "geotab",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  tesla: {
    method: "oauth",
    oauthProvider: "tesla",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["openid", "offline_access", "user_data", "vehicle_device_data"],
  },
  fleet_management: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Healthcare (Restricted) - Batch 20
  fhir: {
    method: "oauth",
    oauthProvider: "fhir",
    supportsOAuth: true,
    supportsApiKey: true,
    defaultMethod: "oauth",
    scopes: ["system/*.read", "system/*.write"],
  },
  hl7: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  epic: {
    method: "oauth",
    oauthProvider: "epic",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["system/*.read"],
  },
  cerner: {
    method: "oauth",
    oauthProvider: "cerner",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["system/*.read"],
  },
  apple_healthkit: {
    method: "oauth",
    oauthProvider: "apple",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["health.read", "health.write"],
  },
  google_fit: {
    method: "oauth",
    oauthProvider: "google",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["https://www.googleapis.com/auth/fitness.activity.read"],
  },
  garmin: {
    method: "oauth",
    oauthProvider: "garmin",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read", "write"],
  },
  oura: {
    method: "oauth",
    oauthProvider: "oura",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["personal", "daily"],
  },
  whoop: {
    method: "oauth",
    oauthProvider: "whoop",
    supportsOAuth: true,
    supportsApiKey: false,
    defaultMethod: "oauth",
    scopes: ["read:recovery", "read:workout"],
  },
  
  // Government / Public Data - Batch 20
  data_gov: {
    method: "none",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  business_registry: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  court_database: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  
  // Automation - Batch 20
  zapier: {
    method: "webhook",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
  make: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  n8n: {
    method: "api-key",
    supportsOAuth: false,
    supportsApiKey: true,
    defaultMethod: "api-key",
  },
  webhook: {
    method: "webhook",
    supportsOAuth: false,
    supportsApiKey: false,
    defaultMethod: "api-key",
  },
};

const DEFAULT_AUTH_CONFIG: IntegrationAuthConfig = {
  method: "api-key",
  supportsOAuth: false,
  supportsApiKey: true,
  defaultMethod: "api-key",
};

/** Get auth config for an integration type; returns default when not in map. */
export function getIntegrationAuthConfig(type: string): IntegrationAuthConfig {
  return INTEGRATION_AUTH_MAP[type] ?? DEFAULT_AUTH_CONFIG;
}

